import React from "react";
import { Link } from "react-router-dom";
import { NAV_GROUPS } from "../../lib/navigation";
import { LOGO } from "../../lib/images";
import { MapPin, Mail, Youtube, Instagram, Facebook } from "lucide-react";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M16.5 3c.3 2.1 1.6 3.7 3.7 3.9v2.4c-1.3.1-2.5-.3-3.7-1v5.9c0 3.4-2.5 5.8-5.7 5.8-3 0-5.3-2.3-5.3-5.2 0-3 2.4-5.3 5.7-5v2.5c-.4-.1-.9-.2-1.3-.1-1.3.2-2.1 1.1-2 2.5.1 1.3 1.1 2.1 2.4 2 1.4-.1 2.1-1.1 2.1-2.6V3h3.4z" />
  </svg>
);

const SOCIALS = [
  { label: "YouTube", href: "https://www.youtube.com/@Asanight-Worldwide?sub_confirmation=1", Icon: Youtube },
  { label: "Instagram", href: "https://www.instagram.com/asanightworldwide", Icon: Instagram },
  { label: "TikTok", href: "https://www.tiktok.com/@asanight", Icon: TikTokIcon },
  { label: "Facebook", href: "https://www.facebook.com/share/1c74FSyHht/", Icon: Facebook },
];

export default function Footer() {
  return (
    <footer className="bg-night text-cream/70 grain-overlay">
      <div className="max-w-7xl mx-auto px-6 py-20 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <img src={LOGO} alt="ÀsÀNigHT Worldwide" className="w-12 h-12 rounded-full object-cover ring-1 ring-gold/40" />
            <span className="font-serif text-2xl text-cream">ÀsÀNigHT</span>
          </div>
          <p className="text-sm leading-relaxed text-cream/60 max-w-xs">
            A cultural platform preserving and sharing Yorùbá traditions, heritage, and
            philosophy with a global audience.
          </p>
          <p className="mt-4 font-serif italic text-gold/80 text-lg">
            “Culture is the total way of life.”
          </p>
          <p className="flex items-center gap-2 text-sm mt-6 text-cream/50">
            <MapPin className="w-4 h-4 text-gold" />
            Pawtucket, Rhode Island
          </p>
          <div className="mt-4 space-y-2">
            <a href="mailto:isholaatolagbe@asanightworldwide.com" className="flex items-center gap-2 text-sm text-cream/50 hover:text-gold transition-colors break-all" data-testid="footer-email-1">
              <Mail className="w-4 h-4 text-gold shrink-0" />
              isholaatolagbe@asanightworldwide.com
            </a>
            <a href="mailto:asanight.worldwide@gmail.com" className="flex items-center gap-2 text-sm text-cream/50 hover:text-gold transition-colors break-all" data-testid="footer-email-2">
              <Mail className="w-4 h-4 text-gold shrink-0" />
              asanight.worldwide@gmail.com
            </a>
          </div>

          <div className="mt-6" data-testid="footer-socials">
            <p className="text-xs uppercase tracking-[0.2em] text-cream/40 mb-3">Follow Us</p>
            <div className="flex items-center gap-3">
              {SOCIALS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="grid place-items-center w-10 h-10 rounded-full border border-cream/15 text-cream/70 hover:text-night hover:bg-gold hover:border-gold transition-colors"
                >
                  <Icon className="w-[18px] h-[18px]" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <h3 className="text-cream font-serif text-lg mb-4">{group.label}</h3>
            <ul className="space-y-2.5">
              {group.items.slice(0, 6).map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-sm text-cream/60 hover:text-gold transition-colors"
                    data-testid={`footer-link-${item.to}`}
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-cream/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-cream/40">
          <p>© {new Date().getFullYear()} ÀsÀNigHT Worldwide. Where Yorùbá Tradition Meets the World.</p>
          <div className="flex gap-5">
            <Link to="/about" className="hover:text-gold transition-colors">About</Link>
            <Link to="/blog" className="hover:text-gold transition-colors">Journal</Link>
            <Link to="/login" className="hover:text-gold transition-colors" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
